/**
 * Square rest-break pay: allotted expected duration is paid; time after that
 * is unpaid when the location converts paid-break overages.
 */

export function parseIsoDurationToSeconds(value) {
  if (value == null || value === "") return null;
  if (typeof value === "number" && Number.isFinite(value)) {
    return Math.max(0, Math.round(value));
  }
  const s = String(value).trim().toUpperCase();
  if (!s) return null;
  if (/^\d+$/.test(s)) return Number(s);
  const match = s.match(
    /^P(?:(\d+)Y)?(?:(\d+)M)?(?:(\d+)D)?(?:T(?:(\d+)H)?(?:(\d+)M)?(?:(\d+(?:\.\d+)?)S)?)?$/,
  );
  if (!match) return null;
  const years = Number(match[1] || 0);
  const months = Number(match[2] || 0);
  const days = Number(match[3] || 0);
  const hours = Number(match[4] || 0);
  const minutes = Number(match[5] || 0);
  const seconds = Number(match[6] || 0);
  return (
    years * 365 * 24 * 3600 +
    months * 30 * 24 * 3600 +
    days * 86400 +
    hours * 3600 +
    minutes * 60 +
    Math.round(seconds)
  );
}

export function parseIsoDurationToMinutes(value) {
  const seconds = parseIsoDurationToSeconds(value);
  if (seconds == null) return null;
  return Math.round(seconds / 60);
}

function asDate(value) {
  if (!value) return null;
  const d = value instanceof Date ? value : new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

export function isBreakPaid(breakItem) {
  if (!breakItem || typeof breakItem !== "object") return false;
  if (breakItem.isPaid !== undefined) return !!breakItem.isPaid;
  if (breakItem.is_paid !== undefined) return !!breakItem.is_paid;
  return false;
}

export function breakPaySplit(breakItem) {
  const start = asDate(breakItem?.startAt || breakItem?.start_at);
  const end = asDate(breakItem?.endAt || breakItem?.end_at);
  const paid = isBreakPaid(breakItem);
  if (!start || !end || end <= start) {
    const expected = parseIsoDurationToMinutes(
      breakItem?.expectedDuration || breakItem?.expected_duration,
    );
    return {
      actualMinutes: expected || 0,
      paidMinutes: paid ? expected || 0 : 0,
      unpaidMinutes: paid ? 0 : expected || 0,
      isPaid: paid,
    };
  }
  const actualMinutes = Math.round((end.getTime() - start.getTime()) / 60000);
  if (!paid) {
    return {
      actualMinutes,
      paidMinutes: 0,
      unpaidMinutes: actualMinutes,
      isPaid: false,
    };
  }
  const expected = parseIsoDurationToMinutes(
    breakItem?.expectedDuration || breakItem?.expected_duration,
  );
  if (expected == null) {
    return {
      actualMinutes,
      paidMinutes: actualMinutes,
      unpaidMinutes: 0,
      isPaid: true,
    };
  }
  const paidMinutes = Math.min(actualMinutes, expected);
  return {
    actualMinutes,
    paidMinutes,
    unpaidMinutes: Math.max(0, actualMinutes - expected),
    isPaid: true,
  };
}

export function unpaidBreakMinutes(breakItem) {
  const start = asDate(breakItem?.startAt || breakItem?.start_at);
  const end = asDate(breakItem?.endAt || breakItem?.end_at);
  if (!start || !end) return 0;
  return breakPaySplit(breakItem).unpaidMinutes;
}

/**
 * Paid vs unpaid time windows on a break. Paid rest-break overage is a
 * second unpaid window after the allotted expected duration.
 */
export function splitBreakPayWindows(breakItem, fallbackEnd) {
  const start = asDate(breakItem?.startAt || breakItem?.start_at);
  if (!start) return [];
  const end =
    asDate(breakItem?.endAt || breakItem?.end_at) || asDate(fallbackEnd);
  if (!end || end <= start) return [];

  if (!isBreakPaid(breakItem)) {
    return [{ start, end, isPaid: false }];
  }

  const expectedSeconds = parseIsoDurationToSeconds(
    breakItem?.expectedDuration || breakItem?.expected_duration,
  );
  if (expectedSeconds == null) {
    return [{ start, end, isPaid: true }];
  }

  const paidEnd = new Date(start.getTime() + expectedSeconds * 1000);
  if (paidEnd <= start) {
    return [{ start, end, isPaid: false }];
  }
  if (end <= paidEnd) {
    return [{ start, end, isPaid: true }];
  }
  return [
    { start, end: paidEnd, isPaid: true },
    { start: paidEnd, end, isPaid: false },
  ];
}

export function formatBreakTypeLabel(breakType) {
  const name = (breakType && breakType.name) || "Break";
  const minutes = parseIsoDurationToMinutes(
    breakType?.expectedDuration || breakType?.expected_duration,
  );
  const paid = isBreakPaid(breakType);
  if (minutes != null) {
    return `${name} (${minutes} min ${paid ? "paid" : "unpaid"})`;
  }
  return `${name} (${paid ? "Paid" : "Unpaid"})`;
}

export function formatBreakPayStatus(breakItem) {
  const split = breakPaySplit(breakItem);
  if (!split.isPaid) return "Unpaid";
  if (split.unpaidMinutes > 0) {
    return `${split.paidMinutes}m paid, ${split.unpaidMinutes}m unpaid`;
  }
  return "Paid";
}

export function breakTypesForLocation(
  breakTypes,
  locationId,
  selectedTypeIds = [],
) {
  const list = Array.isArray(breakTypes) ? breakTypes : [];
  if (!locationId) return list;
  const loc = String(locationId);
  const forLoc = list.filter(
    (bt) => !bt.locationId || String(bt.locationId) === loc,
  );
  const byId = new Map(list.map((bt) => [String(bt.id), bt]));
  const extra = [];
  for (const id of selectedTypeIds) {
    if (!id) continue;
    const key = String(id);
    if (forLoc.some((bt) => String(bt.id) === key)) continue;
    const bt = byId.get(key);
    if (bt) extra.push(bt);
  }
  if (forLoc.length === 0 && extra.length === 0) return list;
  return extra.length ? [...forLoc, ...extra] : forLoc;
}
